/* eslint-disable id-blacklist */

import { gql } from '@apollo/client';
import * as Apollo from '@apollo/client';

import type * as Types from './operations';

const defaultOptions = {} as const;
export const AmericanFootballFragmentDoc = gql`
  fragment AmericanFootball on AmericanFootballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      timeElapsed
      timeRemaining
    }
    quarterScores {
      period
      score {
        home
        away
      }
    }
  }
`;
export const BaseballFragmentDoc = gql`
  fragment Baseball on BaseballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
    }
    scorePerInning {
      period
      score {
        home
        away
      }
    }
  }
`;
export const BasketballFragmentDoc = gql`
  fragment Basketball on BasketballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      segment
      timeElapsed
      timeRemaining
    }
    periodScores {
      period
      segment
      score {
        home
        away
      }
    }
  }
`;
export const HandballFragmentDoc = gql`
  fragment Handball on HandballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      timeElapsed
    }
  }
`;
export const DartsFragmentDoc = gql`
  fragment Darts on DartsFixture {
    id
    type
    score {
      ... on DartsScore {
        home
        away
      }
      ... on DartsFixtureSetsScore {
        currentSet {
          score {
            home
            away
          }
        }
        setsWon {
          home
          away
        }
      }
    }
  }
`;
export const VolleyballFragmentDoc = gql`
  fragment Volleyball on VolleyballFixture {
    id
    currentSet {
      number
      currentServer
      score {
        home
        away
      }
    }
    previousSets {
      number
      score {
        home
        away
      }
    }
  }
`;
export const ESportsFragmentDoc = gql`
  fragment ESports on EsportsFixture {
    id
    score {
      home
      away
    }
  }
`;
export const IceHockeyFragmentDoc = gql`
  fragment IceHockey on IceHockeyFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      timeElapsed
      timeRemaining
    }
    periodScores {
      period
      score {
        home
        away
      }
    }
  }
`;
export const SnookerFragmentDoc = gql`
  fragment Snooker on SnookerFixture {
    id
    score {
      home
      away
    }
  }
`;
export const TableTennisFragmentDoc = gql`
  fragment TableTennis on TableTennisFixture {
    id
    currentSet {
      number
      currentServer
      score {
        home
        away
      }
    }
    setsWon {
      home
      away
    }
    previousSets {
      number
      score {
        home
        away
      }
    }
  }
`;
export const TennisFragmentDoc = gql`
  fragment Tennis on TennisMatch {
    id
    teamAScore
    teamBScore
    currentSet {
      teamAScore
      teamBScore
      currentGame {
        teamAScore
        teamBScore
        teamServing
        type
      }
    }
    previousSets {
      teamAScore
      teamBScore
    }
    status {
      reason
      status
    }
  }
`;
export const FootballPlayerGameStatsFragmentDoc = gql`
  fragment FootballPlayerGameStats on FootballGameStats {
    fouls
    goals
    passes
    shotsOnTarget
    totalShots
    assists
    totalCards
  }
`;
export const FootballPlayerStatsFragmentDoc = gql`
  fragment FootballPlayerStats on FootballPlayer {
    stats(
      periods: [
        { period: REGULAR, periodStatus: FULL }
        { period: REGULAR, periodStatus: INPLAY_FIRST_HALF }
        { period: REGULAR, periodStatus: INPLAY_SECOND_HALF }
      ]
    ) {
      periodDetails {
        period
        periodStatus
      }
      stats {
        ...FootballPlayerGameStats
      }
    }
  }
  ${FootballPlayerGameStatsFragmentDoc}
`;
export const FootballPlayerFragmentDoc = gql`
  fragment FootballPlayer on FootballPlayer {
    id
    name
    substitutions {
      player {
        name
        id
        ...FootballPlayerStats
        substitutions {
          player {
            name
            id
            ...FootballPlayerStats
            substitutions {
              player {
                name
                id
                ...FootballPlayerStats
                substitutions {
                  player {
                    name
                    id
                    ...FootballPlayerStats
                    substitutions {
                      player {
                        id
                        name
                        ...FootballPlayerStats
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    ...FootballPlayerStats
  }
  ${FootballPlayerStatsFragmentDoc}
`;
export const FootballGameStatsFragmentDoc = gql`
  fragment FootballGameStats on FootballGameStats {
    corners
    goals
    totalCards
    shotsOnTarget
    shotsOffTarget
    totalShots
    possession
    fouls
    offsides
    yellowCards
    redCards
    goalkeeperSaves
  }
`;
export const FootballTeamStatsFragmentDoc = gql`
  fragment FootballTeamStats on FootballFixture {
    stats(
      periods: [
        { periodStatus: FULL }
        { period: EXTRA, periodStatus: INPLAY_FIRST_HALF }
        { period: EXTRA, periodStatus: INPLAY_SECOND_HALF }
        { period: REGULAR, periodStatus: FULL }
        { period: REGULAR, periodStatus: INPLAY_FIRST_HALF }
        { period: REGULAR, periodStatus: INPLAY_SECOND_HALF }
      ]
    ) {
      period
      periodStatus
      home {
        ...FootballGameStats
      }
      away {
        ...FootballGameStats
      }
      both {
        ...FootballGameStats
      }
    }
  }
  ${FootballGameStatsFragmentDoc}
`;
export const FootballIncidentsFragmentDoc = gql`
  fragment FootballIncidents on FootballFixture {
    incidents {
      period
      periodStatus
      clock {
        minute
        roundMinute
      }
      details {
        ... on GoalIncident {
          goalType
          side
        }
        ... on CardIncident {
          cardType
          side
        }
      }
    }
  }
`;
export const FootballFragmentDoc = gql`
  fragment Football on FootballFixture {
    id
    away {
      name
    }
    home {
      name
    }
    score {
      home
      away
    }
    duration {
      period
      status
      clock {
        minute
        roundMinute
      }
    }
    status
    penaltyShootout {
      firstTeamToShoot
      penaltyFormat
      nextTeamToShoot
      penaltyScores {
        penaltyNumber
        side
        shotResult
      }
    }
    players(ids: $footballPlayerIds) @include(if: $includePlayers) {
      ...FootballPlayer
    }
    ...FootballTeamStats @include(if: $includeTeamStats)
    ...FootballIncidents @include(if: $includeIncidents)
  }
  ${FootballPlayerFragmentDoc}
  ${FootballTeamStatsFragmentDoc}
  ${FootballIncidentsFragmentDoc}
`;
export const FootballSettledFragmentDoc = gql`
  fragment FootballSettled on FootballFixture {
    id
    players(ids: $footballPlayerIds) @include(if: $includePlayers) {
      ...FootballPlayer
    }
    ...FootballTeamStats @include(if: $includeTeamStats)
    ...FootballIncidents @include(if: $includeIncidents)
  }
  ${FootballPlayerFragmentDoc}
  ${FootballTeamStatsFragmentDoc}
  ${FootballIncidentsFragmentDoc}
`;
export const LiveDataDocument = gql`
  query LiveData(
    $americanFootballEventIds: [ID!]!
    $baseballEventIds: [ID!]!
    $basketballEventIds: [ID!]!
    $dartsEventIds: [ID!]!
    $eSportsEventIds: [ID!]!
    $footballEventIds: [ID!]!
    $handballEventIds: [ID!]!
    $iceHockeyEventIds: [ID!]!
    $snookerEventIds: [ID!]!
    $tableTennisEventIds: [ID!]!
    $tennisEventIds: [ID!]!
    $volleyballEventIds: [ID!]!
    $footballPlayerIds: [ID!]!
    $includeTeamStats: Boolean!
    $includePlayers: Boolean!
    $includeIncidents: Boolean!
  ) {
    americanFootball {
      fixture(ids: $americanFootballEventIds) {
        ...AmericanFootball
      }
    }
    baseball {
      fixture(ids: $baseballEventIds) {
        ...Baseball
      }
    }
    basketball {
      fixture(ids: $basketballEventIds) {
        ...Basketball
      }
    }
    darts {
      fixture(ids: $dartsEventIds) {
        ...Darts
      }
    }
    esports {
      fixture(ids: $eSportsEventIds) {
        ...ESports
      }
    }
    football {
      fixture(ids: $footballEventIds) {
        ...Football
      }
    }
    handball {
      fixture(ids: $handballEventIds) {
        ...Handball
      }
    }
    iceHockey {
      fixture(ids: $iceHockeyEventIds) {
        ...IceHockey
      }
    }
    snooker {
      fixture(ids: $snookerEventIds) {
        ...Snooker
      }
    }
    tableTennis {
      fixture(ids: $tableTennisEventIds) {
        ...TableTennis
      }
    }
    tennis {
      match(ids: $tennisEventIds) {
        ...Tennis
      }
    }
    volleyball {
      fixture(ids: $volleyballEventIds) {
        ...Volleyball
      }
    }
  }
  ${AmericanFootballFragmentDoc}
  ${BaseballFragmentDoc}
  ${BasketballFragmentDoc}
  ${DartsFragmentDoc}
  ${ESportsFragmentDoc}
  ${FootballFragmentDoc}
  ${HandballFragmentDoc}
  ${IceHockeyFragmentDoc}
  ${SnookerFragmentDoc}
  ${TableTennisFragmentDoc}
  ${TennisFragmentDoc}
  ${VolleyballFragmentDoc}
`;

/**
 * __useLiveDataQuery__
 *
 * To run a query within a React component, call `useLiveDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useLiveDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useLiveDataQuery({
 *   variables: {
 *      americanFootballEventIds: // value for 'americanFootballEventIds'
 *      baseballEventIds: // value for 'baseballEventIds'
 *      basketballEventIds: // value for 'basketballEventIds'
 *      dartsEventIds: // value for 'dartsEventIds'
 *      eSportsEventIds: // value for 'eSportsEventIds'
 *      footballEventIds: // value for 'footballEventIds'
 *      handballEventIds: // value for 'handballEventIds'
 *      iceHockeyEventIds: // value for 'iceHockeyEventIds'
 *      snookerEventIds: // value for 'snookerEventIds'
 *      tableTennisEventIds: // value for 'tableTennisEventIds'
 *      tennisEventIds: // value for 'tennisEventIds'
 *      volleyballEventIds: // value for 'volleyballEventIds'
 *      footballPlayerIds: // value for 'footballPlayerIds'
 *      includeTeamStats: // value for 'includeTeamStats'
 *      includePlayers: // value for 'includePlayers'
 *      includeIncidents: // value for 'includeIncidents'
 *   },
 * });
 */
export function useLiveDataQuery(
  baseOptions: Apollo.QueryHookOptions<Types.LiveDataQuery, Types.LiveDataQueryVariables> &
    ({ variables: Types.LiveDataQueryVariables; skip?: boolean } | { skip: boolean })
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<Types.LiveDataQuery, Types.LiveDataQueryVariables>(
    LiveDataDocument,
    options
  );
}
export function useLiveDataLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<Types.LiveDataQuery, Types.LiveDataQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<Types.LiveDataQuery, Types.LiveDataQueryVariables>(
    LiveDataDocument,
    options
  );
}
export function useLiveDataSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<Types.LiveDataQuery, Types.LiveDataQueryVariables>
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<Types.LiveDataQuery, Types.LiveDataQueryVariables>(
    LiveDataDocument,
    options
  );
}
export type LiveDataQueryHookResult = ReturnType<typeof useLiveDataQuery>;
export type LiveDataLazyQueryHookResult = ReturnType<typeof useLiveDataLazyQuery>;
export type LiveDataSuspenseQueryHookResult = ReturnType<typeof useLiveDataSuspenseQuery>;
export type LiveDataQueryResult = Apollo.QueryResult<
  Types.LiveDataQuery,
  Types.LiveDataQueryVariables
>;
export const SettledDataDocument = gql`
  query SettledData(
    $footballEventIds: [ID!]!
    $footballPlayerIds: [ID!]!
    $includeTeamStats: Boolean!
    $includePlayers: Boolean!
    $includeIncidents: Boolean!
  ) {
    football {
      fixture(ids: $footballEventIds) {
        ...FootballSettled
      }
    }
  }
  ${FootballSettledFragmentDoc}
`;

/**
 * __useSettledDataQuery__
 *
 * To run a query within a React component, call `useSettledDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useSettledDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSettledDataQuery({
 *   variables: {
 *      footballEventIds: // value for 'footballEventIds'
 *      footballPlayerIds: // value for 'footballPlayerIds'
 *      includeTeamStats: // value for 'includeTeamStats'
 *      includePlayers: // value for 'includePlayers'
 *      includeIncidents: // value for 'includeIncidents'
 *   },
 * });
 */
export function useSettledDataQuery(
  baseOptions: Apollo.QueryHookOptions<Types.SettledDataQuery, Types.SettledDataQueryVariables> &
    ({ variables: Types.SettledDataQueryVariables; skip?: boolean } | { skip: boolean })
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<Types.SettledDataQuery, Types.SettledDataQueryVariables>(
    SettledDataDocument,
    options
  );
}
export function useSettledDataLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<Types.SettledDataQuery, Types.SettledDataQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<Types.SettledDataQuery, Types.SettledDataQueryVariables>(
    SettledDataDocument,
    options
  );
}
export function useSettledDataSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<Types.SettledDataQuery, Types.SettledDataQueryVariables>
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<Types.SettledDataQuery, Types.SettledDataQueryVariables>(
    SettledDataDocument,
    options
  );
}
export type SettledDataQueryHookResult = ReturnType<typeof useSettledDataQuery>;
export type SettledDataLazyQueryHookResult = ReturnType<typeof useSettledDataLazyQuery>;
export type SettledDataSuspenseQueryHookResult = ReturnType<typeof useSettledDataSuspenseQuery>;
export type SettledDataQueryResult = Apollo.QueryResult<
  Types.SettledDataQuery,
  Types.SettledDataQueryVariables
>;
